import type { RequirementFlagFacts, RequirementFlagRow } from '@repo/sdk/gen'
import type { BadgeVariant } from '@repo/ui/badge'
import { formatMinor } from '@repo/ui/money'
import type { TKey } from '@/lib/i18n'

type Code = RequirementFlagRow['code']
type State = RequirementFlagRow['state']

export const FLAG_CODE_LABEL = {
	iseeu_above: 'admin-flags:code.iseeu_above',
	ispeu_above: 'admin-flags:code.ispeu_above',
	cfu_below_min: 'admin-flags:code.cfu_below_min',
	offsite_not_met: 'admin-flags:code.offsite_not_met'
} as const satisfies Record<Code, TKey>

export const FLAG_STATE_LABEL = {
	open: 'admin-flags:state.open',
	awaiting_clarification: 'admin-flags:state.awaiting_clarification',
	clarification_received: 'admin-flags:state.clarification_received',
	clarification_expired: 'admin-flags:state.clarification_expired',
	resolved_confirmed: 'admin-flags:state.resolved_confirmed',
	resolved_corrected: 'admin-flags:state.resolved_corrected'
} as const satisfies Record<State, TKey>

/** What waits for the manager stands out; what waits for the student or is closed does not. */
export const FLAG_STATE_TONE = {
	open: 'warning',
	awaiting_clarification: 'neutral',
	clarification_received: 'warning',
	clarification_expired: 'warning',
	resolved_confirmed: 'neutral',
	resolved_corrected: 'neutral'
} as const satisfies Record<State, BadgeVariant>

export const OFF_SITE_LABEL = {
	off_site: 'admin-flags:off_site.off_site',
	commuter: 'admin-flags:off_site.commuter',
	in_site: 'admin-flags:off_site.in_site'
} as const satisfies Record<
	NonNullable<
		Extract<
			RequirementFlagFacts,
			{ code: 'offsite_not_met' }
		>['declaredValue']
	>,
	TKey
>

/**
 * The value declared and the threshold of the call, each in the type of its
 * code: euro for ISEEU and ISPEU, credits, the off-site status.
 */
export function flagFacts(
	facts: RequirementFlagFacts,
	t: (key: TKey, options?: Record<string, unknown>) => string,
	language: string
) {
	const euro = (cents: number) => formatMinor(cents, 'EUR', language)
	const notGiven = t('admin-flags:facts.not_given')
	if (facts.code === 'iseeu_above' || facts.code === 'ispeu_above') {
		return {
			declared:
				facts.declaredValue === null
					? notGiven
					: euro(facts.declaredValue),
			threshold:
				facts.threshold.q70Cents === null
					? t('admin-flags:facts.ceiling_q30', {
							q30: euro(facts.threshold.q30Cents)
						})
					: t('admin-flags:facts.ceilings', {
							q30: euro(facts.threshold.q30Cents),
							q70: euro(facts.threshold.q70Cents)
						})
		}
	}
	if (facts.code === 'cfu_below_min') {
		return {
			declared:
				facts.declaredValue === null
					? notGiven
					: t('admin-flags:facts.credits', {
							count: facts.declaredValue
						}),
			threshold: t('admin-flags:facts.credits_min', {
				count: facts.threshold.credits
			})
		}
	}
	return {
		declared:
			facts.declaredValue === null
				? notGiven
				: t(OFF_SITE_LABEL[facts.declaredValue]),
		threshold: t('admin-flags:facts.off_site_required')
	}
}

/** True for an off-site status, the value a corrected off-site flag carries. */
export function isOffSite(value: string): value is keyof typeof OFF_SITE_LABEL {
	return value in OFF_SITE_LABEL
}
